import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  VideoCameraIcon,
  DocumentIcon,
  PhotoIcon,
  PlusIcon,
  TrashIcon,
  ArrowTopRightOnSquareIcon,
  ArrowDownTrayIcon,
  UserGroupIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { LearningSection, LearningContent, LearningContentType } from '../../types';
import { learningAPI } from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import LearningVideoModal from './LearningVideoModal';
import LearningContentModal from './LearningContentModal';
import ConfirmDialog from '../common/ConfirmDialog';
import toast from 'react-hot-toast';

interface LearningSectionDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  section: LearningSection | null;
  onSectionUpdate?: (section: LearningSection) => void;
  onDataChange?: () => void;
}

const LearningSectionDetailModal: React.FC<LearningSectionDetailModalProps> = ({
  isOpen,
  onClose,
  section,
  onSectionUpdate,
  onDataChange,
}) => {
  const [content, setContent] = useState<LearningContent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isContentModalOpen, setIsContentModalOpen] = useState(false);
  const [editingContent, setEditingContent] = useState<LearningContent | null>(null);
  const [contentToDelete, setContentToDelete] = useState<LearningContent | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (section && isOpen) {
      loadContent();
    }
  }, [section, isOpen]);

  const loadContent = async () => {
    if (!section) return;

    try {
      setIsLoading(true);
      const contentData = await learningAPI.getAllContent(section.id);
      setContent(contentData);
    } catch (error) {
      console.error('Error loading content:', error);
      toast.error('Failed to load content');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddVideo = async (videoData: any) => {
    if (!section) return;

    try {
      setIsSubmitting(true);
      await learningAPI.addVideo({ ...videoData, sectionId: section.id });
      await loadContent();
      setIsVideoModalOpen(false);
      if (onDataChange) onDataChange();
      toast.success('Video added successfully');
    } catch (error) {
      console.error('Error adding video:', error);
      toast.error('Failed to add video');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateVideo = async (videoData: any) => {
    if (!editingContent) return;

    try {
      setIsSubmitting(true);
      await learningAPI.updateVideo(editingContent.id, videoData);
      await loadContent();
      setIsVideoModalOpen(false);
      setEditingContent(null);
      if (onDataChange) onDataChange();
      toast.success('Video updated successfully');
    } catch (error) {
      console.error('Error updating video:', error);
      toast.error('Failed to update video');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteVideo = (video: any) => {
    setContentToDelete(video);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteVideo = async () => {
    if (!contentToDelete) return;

    try {
      setIsSubmitting(true);
      if (contentToDelete.contentType === LearningContentType.VIDEO) {
        await learningAPI.deleteVideo(contentToDelete.id);
      } else {
        await learningAPI.deleteContent(contentToDelete.id);
      }
      await loadContent();
      setIsDeleteDialogOpen(false);
      setContentToDelete(null);
      if (onDataChange) onDataChange();
      toast.success('Content deleted successfully');
    } catch (error) {
      console.error('Error deleting content:', error);
      toast.error('Failed to delete content');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditVideoModal = (video: any) => {
    setEditingContent(video);
    setIsVideoModalOpen(true);
  };

  const closeVideoModal = () => {
    setIsVideoModalOpen(false);
    setEditingContent(null);
  };

  // New content handlers
  const handleAddContent = async (contentData: FormData) => {
    try {
      setIsSubmitting(true);
      await learningAPI.addContent(contentData);
      await loadContent();
      setIsContentModalOpen(false);
      toast.success('Content added successfully');
      if (onDataChange) onDataChange();
    } catch (error) {
      console.error('Error adding content:', error);
      toast.error('Failed to add content');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteContent = (content: LearningContent) => {
    setContentToDelete(content);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteContent = async () => {
    if (!contentToDelete) return;

    try {
      setIsSubmitting(true);
      await learningAPI.deleteContent(contentToDelete.id);
      await loadContent();
      setIsDeleteDialogOpen(false);
      setContentToDelete(null);
      toast.success('Content deleted successfully');
      if (onDataChange) onDataChange();
    } catch (error) {
      console.error('Error deleting content:', error);
      toast.error('Failed to delete content');
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeContentModal = () => {
    setIsContentModalOpen(false);
    setEditingContent(null);
  };

  const getVideoThumbnail = (url: string | null | undefined) => {
    // Extract YouTube video ID and return thumbnail URL
    if (url && (url.includes('youtube.com') || url.includes('youtu.be'))) {
      const videoId = url.includes('youtube.com')
        ? new URLSearchParams(url.split('?')[1])?.get('v')
        : url.split('/').pop();

      if (videoId) {
        return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
      }
    }
    return null;
  };

  const getContentTypeIcon = (contentType: LearningContentType) => {
    switch (contentType) {
      case LearningContentType.VIDEO:
        return <VideoCameraIcon className="h-12 w-12 text-blue-500" />;
      case LearningContentType.PDF:
        return <DocumentIcon className="h-12 w-12 text-red-500" />;
      case LearningContentType.IMAGE:
        return <PhotoIcon className="h-12 w-12 text-green-500" />;
      default:
        return <DocumentIcon className="h-12 w-12 text-gray-400" />;
    }
  };

  const getContentTypeLabel = (contentType: LearningContentType) => {
    switch (contentType) {
      case LearningContentType.VIDEO:
        return 'Video';
      case LearningContentType.PDF:
        return 'PDF Document';
      case LearningContentType.IMAGE:
        return 'Image';
      default:
        return 'Content';
    }
  };

  // Component for image preview with blob loading
  const ImagePreview: React.FC<{ content: LearningContent }> = ({ content }) => {
    const [imageUrl, setImageUrl] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>('');

    useEffect(() => {
      if (content.id) {
        learningAPI.getFileBlob(content.id)
          .then(url => {
            setImageUrl(url);
            setLoading(false);
            setError('');
          })
          .catch(err => {
            console.error('Error loading image:', err);
            setError(err.message || 'Failed to load image');
            setLoading(false);
          });
      }

      return () => {
        if (imageUrl) {
          window.URL.revokeObjectURL(imageUrl);
        }
      };
    }, [content.id]);

    if (loading) {
      return (
        <div className="aspect-video bg-gray-200 relative flex items-center justify-center">
          <PhotoIcon className="h-12 w-12 text-gray-400 animate-pulse" />
          <span className="absolute bottom-2 text-xs text-gray-500">Loading...</span>
        </div>
      );
    }

    if (error) {
      return (
        <div className="aspect-video bg-red-50 relative flex items-center justify-center border-2 border-dashed border-red-200">
          <div className="text-center">
            <PhotoIcon className="h-12 w-12 text-red-400 mx-auto mb-2" />
            <p className="text-xs text-red-600">Image not available</p>
            <p className="text-xs text-red-500 mt-1">{error}</p>
          </div>
        </div>
      );
    }

    return (
      <div className="aspect-video bg-gray-200 relative">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={content.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <PhotoIcon className="h-12 w-12 text-gray-400" />
          </div>
        )}
      </div>
    );
  };

  const handlePdfClick = async (content: LearningContent) => {
    try {
      const blobUrl = await learningAPI.getFileBlob(content.id);
      window.open(blobUrl, '_blank');
    } catch (error) {
      console.error('Error opening PDF:', error);
    }
  };

  const renderContentPreview = (content: LearningContent) => {
    switch (content.contentType) {
      case LearningContentType.VIDEO:
        const thumbnail = getVideoThumbnail(content.url);
        return (
          <div className="aspect-video bg-gray-200 relative">
            {thumbnail ? (
              <img
                src={thumbnail}
                alt={content.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <VideoCameraIcon className="h-12 w-12 text-gray-400" />
              </div>
            )}
          </div>
        );

      case LearningContentType.IMAGE:
        return <ImagePreview content={content} />;

      case LearningContentType.PDF:
        return (
          <div className="aspect-video bg-red-50 relative flex items-center justify-center border-2 border-dashed border-red-200 cursor-pointer hover:bg-red-100 transition-colors"
               onClick={() => handlePdfClick(content)}>
            <div className="text-center">
              <DocumentIcon className="h-16 w-16 text-red-500 mx-auto mb-2" />
              <p className="text-sm text-red-600 font-medium">PDF Document</p>
              <p className="text-xs text-red-500 mt-1">Click to view</p>
            </div>
          </div>
        );



      default:
        return (
          <div className="aspect-video bg-gray-200 relative flex items-center justify-center">
            <DocumentIcon className="h-12 w-12 text-gray-400" />
          </div>
        );
    }
  };

  if (!isOpen || !section) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <VideoCameraIcon className="h-6 w-6 text-primary-600" />
              <div>
                <h2 className="text-xl font-semibold text-gray-900">{section.name}</h2>
                <div className="flex items-center space-x-4 mt-1">
                  <div 
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                    style={{ 
                      backgroundColor: `${section.team.color}20`, 
                      color: section.team.color 
                    }}
                  >
                    <UserGroupIcon className="w-3 h-3 mr-1" />
                    {section.team.name}
                  </div>
                  <div className="flex items-center text-xs text-gray-500">
                    <CalendarIcon className="w-3 h-3 mr-1" />
                    Created {new Date(section.createdAt).toLocaleDateString()}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setIsVideoModalOpen(true)}
                className="btn-secondary flex items-center space-x-2"
              >
                <VideoCameraIcon className="h-4 w-4" />
                <span>Add Video</span>
              </button>
              <button
                onClick={() => setIsContentModalOpen(true)}
                className="btn-primary flex items-center space-x-2"
              >
                <PlusIcon className="h-4 w-4" />
                <span>Add Content</span>
              </button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
            {section.description && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Description</h3>
                <p className="text-gray-600">{section.description}</p>
              </div>
            )}

            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Learning Content ({content.length})
              </h3>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner size="lg" />
              </div>
            ) : content.length === 0 ? (
              <div className="text-center py-12">
                <DocumentIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No content yet</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Get started by adding content to this section.
                </p>
                <div className="mt-6 flex justify-center space-x-3">
                  <button
                    onClick={() => setIsVideoModalOpen(true)}
                    className="btn-secondary flex items-center space-x-2"
                  >
                    <VideoCameraIcon className="h-4 w-4" />
                    <span>Add Video</span>
                  </button>
                  <button
                    onClick={() => setIsContentModalOpen(true)}
                    className="btn-primary flex items-center space-x-2"
                  >
                    <PlusIcon className="h-4 w-4" />
                    <span>Add Content</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {content.map((item) => (
                  <div
                    key={item.id}
                    className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                  >
                    {/* Content Preview */}
                    {renderContentPreview(item)}

                    {/* Content Info */}
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {getContentTypeLabel(item.contentType)}
                        </span>
                        <div className="flex items-center space-x-1">
                          {item.contentType === LearningContentType.VIDEO && item.url && (
                            <a
                              href={item.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1 text-blue-600 hover:text-blue-800 transition-colors"
                              title="Watch Video"
                            >
                              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                            </a>
                          )}
                          {item.contentType !== LearningContentType.VIDEO && (
                            <button
                              onClick={() => learningAPI.downloadFile(item.id, item.fileName)}
                              className="p-1 text-green-600 hover:text-green-800 transition-colors"
                              title="Download File"
                            >
                              <ArrowDownTrayIcon className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteContent(item)}
                            className="p-1 text-red-600 hover:text-red-800 transition-colors"
                            title="Delete Content"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      <h4 className="font-medium text-gray-900 mb-2 line-clamp-2">
                        {item.title}
                      </h4>

                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>
                          {new Date(item.createdAt).toLocaleDateString()}
                        </span>
                        {item.fileName && (
                          <span className="truncate ml-2" title={item.fileName}>
                            {item.fileName}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Learning Video Modal */}
      <LearningVideoModal
        isOpen={isVideoModalOpen}
        onClose={closeVideoModal}
        onSubmit={handleAddVideo}
        sectionId={section.id}
        isLoading={isSubmitting}
      />

      {/* Learning Content Modal */}
      <LearningContentModal
        isOpen={isContentModalOpen}
        onClose={closeContentModal}
        onSubmit={handleAddContent}
        sectionId={section.id}
        isLoading={isSubmitting}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          setContentToDelete(null);
        }}
        onConfirm={confirmDeleteContent}
        title="Delete Content"
        message={`Are you sure you want to delete "${contentToDelete?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        isLoading={isSubmitting}
      />
    </>
  );
};

export default LearningSectionDetailModal;
